<script setup lang="ts">
import { $supplierList } from '@/api/users'
import { uniqBy } from 'lodash'
import type { SupplierSelect } from '@/models/supplier'

interface IProps {
  modelValue?: any,
  label?: string,
  division?: any
  projectSuppliersOnly: boolean
  crSuppliersOnly: boolean
  projectId: number
  crId: number
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: () => ({}),
  label: 'Supplier',
  division: undefined,
  projectSuppliersOnly: undefined,
  crSuppliersOnly: undefined,
  projectId: undefined,
  crId: undefined,
})

const emit = defineEmits<{
  'update:modelValue': (newValue: any) => void
}>()

const suppliers = ref<SupplierSelect[]>([])
const suppliersCurrentPage = ref<number>(1)
const suppliersPerPage: number = 10
const suppliersSearchStr = ref<string>('')
const isNextSuppliersPageExist = ref<boolean>(false)
const suppliersLoading = ref<boolean>(false)


const getSuppliers = async () => {
  suppliersLoading.value = true
  try {
    const payloadDivision = props.division !== undefined ? props.division : undefined

    const payload = {
      supplier_name__icontains: suppliersSearchStr.value,
      division: payloadDivision,
      per_page: suppliersPerPage,
      page: suppliersCurrentPage.value,

      project_suppliers_only: props.projectSuppliersOnly,
      cr_suppliers_only: props.crSuppliersOnly,
      project_id: props.projectId,
      cr_id: props.crId
    }

    const res = await $supplierList(payload)

    // Append the new suppliers to the existing list
    // Use Lodash's uniqBy to merge arrays while ensuring uniqueness based on 'id'
    suppliers.value = uniqBy([...suppliers.value, ...res.data.results], 'id')
    isNextSuppliersPageExist.value = !!res.data.next
  }
  catch (e) {
    console.log(e)
  }
  finally {
    suppliersLoading.value = false
  }
}

const onFocusSupplierSelect = async () => {
  if (!suppliers.value.length && !props.modelValue) {
    suppliers.value = []
    suppliersCurrentPage.value = 1
    await getSuppliers()
  }
}

const onLoadMoreSuppliers = async () => {
  suppliersCurrentPage.value++ // Increment the current page to load the next page of suppliers
  await getSuppliers()
}

const handleSupplierSearch = useDebounceFn((searchStr: string) => {
  suppliers.value = []
  suppliersCurrentPage.value = 1 // Set current page => 1 to load initial first page of suppliers
  suppliersSearchStr.value = searchStr
  getSuppliers()
}, 500)

const updateSuppliersList = () => {
  suppliers.value = []
  suppliersCurrentPage.value = 1
  getSuppliers()
}

watch(() => props.division, () => {
  updateSuppliersList()
})

watch(() => props.projectId, () => {
  updateSuppliersList()

})

watch(() => props.crId, () => {
  updateSuppliersList()
})
</script>

<template>
  <VAutocomplete
    :model-value="modelValue"
    :items="[...suppliers]"
    return-object
    item-title="supplier_name"
    item-value="id"
    :label="label"
    density="compact"
    no-filter
    clearable
    @update:modelValue="newValue => $emit('update:modelValue', newValue)"
    @update:focused="onFocusSupplierSelect"
    @update:search="handleSupplierSearch"
  >
    <template #no-data>
      <div
        v-if="!!suppliersLoading"
        class="d-flex justify-center align-center"
      >
        Loading...
        <VProgressCircular
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </div>
      <div
        v-else
        class="d-flex justify-center align-center"
      >
        Suppliers not found
      </div>
    </template>
    <template
      v-if="isNextSuppliersPageExist"
      #append-item
    >
      <VBtn
        color="default"
        block
        :disabled="!!suppliersLoading"
        @click="onLoadMoreSuppliers"
      >
        Load more...
        <VProgressCircular
          v-if="!!suppliersLoading"
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </VBtn>
    </template>
  </VAutocomplete>
</template>
